import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import type { LibraryComponent } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { opsApi, useOpsMutation } from '@/components/ops/api'
import { formatInt, formatPct } from '@/components/ops/format'
import { ErrorState, FormError, LoadingState } from '@/components/app/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { cn } from '@/lib/utils'

const STATUSES = ['proposed', 'approved', 'rejected', 'builtin'] as const
type Status = LibraryComponent['status']

export const Route = createFileRoute('/ops/library')({
  validateSearch: z.object({ status: z.enum(STATUSES).optional().catch(undefined) }),
  loader: () => opsApi<{ components: LibraryComponent[] }>('/ops/library').then((r) => r.components),
  head: () => ({ meta: [{ title: 'Library · Ops' }] }),
  component: LibraryPage,
  pendingComponent: () => <LoadingState label="Loading library" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the library" />,
})

const TONE: Record<Status, string> = {
  proposed: 'border-warning/40 text-warning',
  approved: 'border-success/40 text-success',
  rejected: 'border-destructive/40 text-destructive',
  builtin: 'border-border text-muted-foreground',
}

function LibraryPage() {
  const components = Route.useLoaderData()
  const { status } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { run, pending, error } = useOpsMutation()
  const [busy, setBusy] = useState<string | null>(null)

  const rows = components
    .filter((c) => !status || c.status === status)
    .sort((a, b) => Number(b.status === 'proposed') - Number(a.status === 'proposed') || b.usageCount - a.usageCount)
  const proposed = components.filter((c) => c.status === 'proposed').length

  async function setStatus(c: LibraryComponent, next: 'approved' | 'rejected') {
    setBusy(c.id)
    await run(() => api(`/ops/library/${encodeURIComponent(c.id)}`, { method: 'POST', json: { status: next } }))
    setBusy(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="mr-auto text-base font-semibold">
          Widget library <span className="font-normal text-muted-foreground">({components.length}, {proposed} proposed)</span>
        </h1>
        <div className="flex flex-col gap-1">
          <Label htmlFor="lib-status" className="text-xs text-muted-foreground">
            Status
          </Label>
          <NativeSelect
            id="lib-status"
            value={status ?? ''}
            onChange={(e) => navigate({ search: { status: (e.target.value || undefined) as Status | undefined }, replace: true })}
          >
            <NativeSelectOption value="">All</NativeSelectOption>
            {STATUSES.map((s) => (
              <NativeSelectOption key={s} value={s}>
                {s}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
      </div>
      <FormError message={error} />
      {rows.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>No components</EmptyTitle>
            <EmptyDescription>{status ? `No ${status} component.` : 'Components appear after the first builds propose them.'}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Widget</TableHead>
                <TableHead>Builder</TableHead>
                <TableHead>Versions</TableHead>
                <TableHead className="text-right">Sites</TableHead>
                <TableHead className="text-right">Pass rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="py-1 font-medium">{c.widget}</TableCell>
                  <TableCell className="py-1">{c.builder}</TableCell>
                  <TableCell className="py-1 text-muted-foreground">{c.versions ?? '—'}</TableCell>
                  <TableCell className="py-1 text-right tabular-nums">{formatInt(c.usageCount)}</TableCell>
                  <TableCell className={cn('py-1 text-right tabular-nums', c.passRate !== null && c.passRate < 0.6 && 'text-destructive')}>
                    {formatPct(c.passRate)}
                  </TableCell>
                  <TableCell className="py-1">
                    <Badge variant="outline" className={TONE[c.status]}>
                      {c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1 text-right whitespace-nowrap">
                    {c.status !== 'builtin' && (
                      <span className="inline-flex gap-1">
                        {c.status !== 'approved' && (
                          <Button size="xs" variant="outline" disabled={pending} onClick={() => setStatus(c, 'approved')} aria-label={`Approve ${c.builder} ${c.widget}`}>
                            {busy === c.id ? '…' : 'Approve'}
                          </Button>
                        )}
                        {c.status !== 'rejected' && (
                          <Button size="xs" variant="destructive" disabled={pending} onClick={() => setStatus(c, 'rejected')} aria-label={`Reject ${c.builder} ${c.widget}`}>
                            Reject
                          </Button>
                        )}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
